import React, { useState } from 'react';
import classNames from 'classnames';
import styles from './BarGraph.module.scss';

interface ChartData {
  group: string;
  series: { name: string; value: number }[];
}

interface BarGraphProps {
  data: ChartData[];
  width?: number;
  height?: number;
  xAxisLegendMargin?: number;
  title?: string;
  subTitle?: string;
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  titleFontSize?: number;
  subTitleFontSize?: number;
  xAxisLabelFontSize?: number;
  yAxisLabelFontSize?: number;
  colors?: string[];
}

const BarGraph: React.FC<BarGraphProps> = ({
  data = [],
  width = 700,
  height = 300,
  xAxisLegendMargin = 40,
  title = "",
  subTitle = "",
  paddingTop = 50,
  paddingBottom = 50,
  paddingLeft = 50,
  paddingRight = 5,
  titleFontSize = 16,
  subTitleFontSize = 14,
  xAxisLabelFontSize = 12,
  yAxisLabelFontSize = 12,
  colors = [
    "var(--color-2)", // ligne 1
    "var(--color-1)", // ligne 2
    "var(--color-3)", // ligne 3
    "var(--color-4)", // ligne 4
    "var(--color-5)"  // ligne 5
  ],
}) => {
  const [, setTooltip] = useState<{ x: number; y: number; value: number; name: string } | null>(null);

  let maxValue = Math.max(...data.flatMap(d => d.series.map(s => s.value)));
  if (maxValue < 5) {
    maxValue = 5;
  }

  const yTicks = Array.from({ length: Math.ceil(maxValue) }, (_, i) => i + 1);

  const availableWidth = width - paddingLeft - paddingRight;
  const availableHeight = height - paddingTop - paddingBottom;

  const totalGroups = data.length;
  const singleSeriesPerGroup = data.every(group => group.series.length === 1);

  const innerGroupSpacing = 5; 
  const groupSpacing = 20; 

  let barWidth = singleSeriesPerGroup
    ? availableWidth / totalGroups
    : (availableWidth - (totalGroups - 1) * groupSpacing - (data.reduce((sum, group) => sum + group.series.length, 0) - totalGroups) * innerGroupSpacing) / data.reduce((sum, group) => sum + group.series.length, 0);

  const groupWidth = (group: ChartData) => {
	  
    return group.series.length * barWidth + (group.series.length - 1) * innerGroupSpacing;
  };

  const tickSpacing = availableHeight / yTicks.length;
  const skipValues = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
  let yLabelSkip = 1;

  for (const skip of skipValues) {
    if (tickSpacing * skip < yAxisLabelFontSize * 1.5) {
      yLabelSkip = skip;
    } else {
      break;
    }
  }

  let labelSkipRatio: number = 1;

  const canLabelsBeStraight = data.every(group => {
    const totalGroupWidth = groupWidth(group);
    return totalGroupWidth >= xAxisLabelFontSize * group.group.length * 0.6;
  });

  // Calculer le padding nécessaire pour les labels inclinés
 // const labelPadding = !canLabelsBeStraight ? xAxisLabelFontSize * ( data.length>0 ? data[0].group.length * 0.8 : 1 ): 0;

  // Ajuster paddingBottom et paddingLeft
  //paddingBottom += labelPadding;
  //paddingLeft   += labelPadding;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="100%"
      aria-labelledby={title}
      role="img"
      onMouseLeave={() => setTooltip(null)}
    >
      <title id="bar-graph-title">{title}</title>

      <text
        x={width / 2}
        y={(paddingTop - titleFontSize - subTitleFontSize - 5) / 2 + titleFontSize / 2}
        textAnchor="middle"
        fontSize={titleFontSize}
        fontWeight="bold"
        fill="var(--color-1)"
      >
        {title}
      </text>

      <text
        x={width / 2}
        y={(paddingTop - titleFontSize - subTitleFontSize - 5) / 2 + titleFontSize + 5 + subTitleFontSize / 2}
        textAnchor="middle"
        fontSize={subTitleFontSize}
        fill="var(--color-1)"
      >
        {subTitle}
      </text>

      {yTicks.map((tick, index) => {
        if (yLabelSkip > 1 && tick % yLabelSkip !== 0) return null;

        const y = height - paddingBottom - (tick / maxValue) * availableHeight;
        return (
          <g key={index}>
            <line
              x1={paddingLeft - 2}
              y1={y}
              x2={width - paddingRight}
              y2={y}
              stroke="black"
              strokeWidth="0.5"
            />
            <text
              x={paddingLeft - 10}
              y={y + 5}
              textAnchor="end"
              fill="black"
              fontSize={yAxisLabelFontSize}
            >
              {tick}
            </text>
          </g>
        );
      })}

      {data.map((group, groupIndex) => {
        if (group.series.length === 0) {
          return (
            <text
              key={`no-data-${groupIndex}`}
              x={paddingLeft + groupIndex * (groupWidth(group) + groupSpacing) + groupWidth(group) / 2}
              y={height - paddingBottom + 15}
              textAnchor="middle"
              fontSize={xAxisLabelFontSize}
              fill="red"
              className={styles.text}
            >
              No Data
            </text>
          );
        }

        return group.series.map((item, seriesIndex) => {
          const barHeight = (item.value / maxValue) * availableHeight;
          const groupX = paddingLeft + groupIndex * (groupWidth(group) + groupSpacing);
          const x = singleSeriesPerGroup ? (paddingLeft + groupIndex * barWidth) : (groupX + seriesIndex * (barWidth + innerGroupSpacing));
          const y = height - paddingBottom - barHeight;

          return (
            <rect
              key={`${groupIndex}-${seriesIndex}`}
              x={x}
              y={y}
              width={singleSeriesPerGroup ? ( barWidth > 2 ? barWidth * 0.8 : barWidth ) : barWidth}
              height={barHeight}
              fill={colors[seriesIndex % colors.length]}
              className={classNames(styles.bar)}
              aria-label={`${group.group} ${item.name}: ${item.value}`}
            />
          );
        });
      })}

      {data.map((group, index) => {
        if (!canLabelsBeStraight) {
          labelSkipRatio = 1;

			   //  console.log (data, group, singleSeriesPerGroup, group.series.length)		

          if (singleSeriesPerGroup)
		        {
			 //    console.log (barWidth, xAxisLabelFontSize * 1.5)		
					
          if (barWidth < xAxisLabelFontSize * 1.5) {
            labelSkipRatio = Math.ceil(xAxisLabelFontSize * 1.5 / barWidth);
			//     console.log (labelSkipRatio)		
          }			   
		        }
		   else {
			     console.log (groupWidth(group), xAxisLabelFontSize * 1.2)		
          if (groupWidth(group) < xAxisLabelFontSize * 1.2) {
            labelSkipRatio = Math.ceil(xAxisLabelFontSize * 1.2 / groupWidth(group));
          }			   
		        }


			     console.log (labelSkipRatio)		

          if (labelSkipRatio !== 1 && index % labelSkipRatio !== 0) {
            return null;
          }
        }

        const groupX = singleSeriesPerGroup
          ? (paddingLeft + index * barWidth + (barWidth * 0.8) / 2)
          : (paddingLeft + index * (groupWidth(group) + groupSpacing) + groupWidth(group) / 2);

        const textY = height - paddingBottom + 15;

        return (
          <React.Fragment key={index}>
            <text
              x={groupX}
              y={textY}
              textAnchor={canLabelsBeStraight ? "middle" : "end"}
              fontSize={xAxisLabelFontSize}
              className={styles.text}
              transform={canLabelsBeStraight ? '' : `rotate(-45 ${groupX} ${textY})`}
            >
              {group.group}
            </text>
            {!canLabelsBeStraight && labelSkipRatio > 1 && (
              <line
                x1={groupX}
                y1={textY - 5}
                x2={groupX}
                y2={height - paddingBottom}
                stroke="black"
                strokeWidth="0.5"
              />
            )}
          </React.Fragment>
        );
      })}

      <line
        x1={paddingLeft - 2}
        y1={height - paddingBottom}
        x2={width - paddingRight}
        y2={height - paddingBottom}
        stroke="black"
        strokeWidth="1"
      />

      <line
        x1={paddingLeft - 2}
        y1={paddingTop}
        x2={paddingLeft - 2}
        y2={height - paddingBottom}
        stroke="black"
        strokeWidth="1"
      />

      {/*
		  tooltip && (
        <g>
          <rect
            x={tooltip.x - 30}
            y={tooltip.y - 20}
            width={60}
            height={20}
            fill="white"
            stroke="black"
          />
          <text
            x={tooltip.x}
            y={tooltip.y}
            textAnchor="middle"
            fontSize="12"
            fill="black"
          >
            {`${tooltip.name}: ${tooltip.value}`}
          </text>
        </g>
      )
	  */}
    </svg>
  );
};

export default BarGraph;
